import React, { Component } from 'react'
import { withFirebase } from '../../components/Fb'
import { Row, Col, Button } from 'reactstrap'
import {
  DataGrid,
  GridToolbar,
  GridToolbarExport,
  GridToolbarContainer,
  GridToolbarFilterButton,
} from '@mui/x-data-grid'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import PersonSearch from '../../components/Inputs/PersonSearch'
import EditButton from '../../components/Buttons/EditButton'
import DeleteButton from '../../components/Buttons/DeleteButton'
import ShareRegistryInfo from '../../components/Infos/ShareRegistryInfo'
import moment from 'moment-timezone'

const service = 'shareregistry'

function ExportToolbar({ suffix }) {
  return (
    <GridToolbarContainer style={{ justifyContent: 'flex-start' }}>
      <GridToolbarFilterButton />
      <GridToolbarExport
        csvOptions={{
          fileName: 'osakerekisteri-' + (suffix || 'export'),
          delimiter: ';',
          utf8WithBom: true,
        }}
      />
    </GridToolbarContainer>
  )
}

class ShareRegistryContainer extends Component {
  constructor(props) {
    super(props)
    this.state = {
      enabled: false,
      saving: false,
      loading: true,
      activeTab: 0,
      shares: [],
      holders: [],
      classes: [],
      templates: [],
      certificates: [],
      // Selection for batch transaction
      selectionModel: { type: 'include', ids: new Set() },
      selectedShareIds: [],
      // Holder dialog state
      holderDialogOpen: false,
      holderForm: { type: 'person', personId: '', personName: '', ssn: '', contactPerson: '', phone: '', email: '' },
      editingHolderId: null,
      // Batch edit dialog state
      batchEditDialogOpen: false,
      batchEditProperties: [],
      // Certificate assign dialog state
      certificateDialogOpen: false,
      certificateAssignId: '',
      // Transaction dialog state
      transactionDialogOpen: false,
      transaction: {
        type: 'buy',
        date: moment().format('YYYY-MM-DD'),
        fromId: '',
        toId: '',
        price: '',
        note: '',
      },
    }
  }

  componentDidMount() {
    if (this.props.modules && this.props.modules[service] && this.props.modules[service].enabled) {
      this.setState({ enabled: true })
    }
    this.loadAllData()
  }

  componentDidUpdate(prevProps) {
    if (this.props.modules && this.props.modules[service]) {
      if (this.props.modules[service].enabled && !prevProps.modules[service]?.enabled) {
        this.setState({ enabled: true })
      }
      if (!this.props.modules[service].enabled && prevProps.modules[service]?.enabled) {
        this.setState({ enabled: false })
      }
    } else {
      if (this.state.enabled) {
        this.setState({ enabled: false })
      }
    }
  }

  enableModule = async (price) => {
    this.setState({ saving: true })
    await this.props.firebase.enableModule(service, price)
  }

  loadAllData = async () => {
    try {
      const [classesSnap, sharesSnap, holdersSnap, templatesSnap, certificatesSnap] = await Promise.all([
        this.props.firebase.getShareClasses(),
        this.props.firebase.getShares(),
        this.props.firebase.getShareholders(),
        this.props.firebase.getShareTemplates(),
        this.props.firebase.getShareCertificates(),
      ])

      const classes = classesSnap.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((r) => r.state !== 'deleted')

      const shares = sharesSnap.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((r) => r.state !== 'deleted')

      const holders = holdersSnap.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((r) => r.state !== 'deleted')

      const templates = templatesSnap.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((r) => r.state !== 'deleted')

      const certificates = certificatesSnap.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((r) => r.state !== 'deleted')

      this.setState({ classes, shares, holders, templates, certificates, loading: false })
    } catch (error) {
      console.error('Error loading share registry data:', error)
      this.setState({ loading: false })
    }
  }

  handleTabChange = (event, newValue) => {
    this.setState({ activeTab: newValue })
  }

  handleDelete = async (id, entityType) => {
    const msg = entityType === 'share' ? 'Haluatko varmasti poistaa osakkeen?' : 'Haluatko varmasti poistaa osakkeenomistajan?'
    if (!window.confirm(msg)) return
    if (entityType === 'share') await this.props.firebase.deleteShare(id)
    else if (entityType === 'holder') await this.props.firebase.deleteShareholder(id)
    this.loadAllData()
  }

  getHolderName = (holder) => {
    if (!holder) return '-'
    if (holder.personName) return holder.personName
    if (holder.email) return holder.email
    return holder.id
  }

  getClassName = (classId) => {
    const cls = this.state.classes.find((c) => c.id === classId)
    return cls ? cls.name : '-'
  }

  getTemplateName = (templateId) => {
    const tpl = this.state.templates.find((t) => t.id === templateId)
    return tpl ? tpl.name : '-'
  }

  getOwnerName = (ownerId) => {
    if (!ownerId) return '-'
    const holder = this.state.holders.find((h) => h.id === ownerId)
    return this.getHolderName(holder)
  }

  getShareCount = (entityId) => {
    return this.state.shares.filter((s) => s.currentOwnerId === entityId).length
  }

  // Holder dialog
  handleAddHolder = () => {
    this.setState({
      holderDialogOpen: true,
      editingHolderId: null,
      holderForm: { type: 'person', personId: '', personName: '', ssn: '', contactPerson: '', phone: '', email: '' },
    })
  }

  handleEditHolder = (id) => {
    const holder = this.state.holders.find((r) => r.id === id)
    this.setState({
      holderDialogOpen: true,
      editingHolderId: id,
      holderForm: {
        type: holder.type || 'person',
        personId: holder.personId || '',
        personName: holder.personName || '',
        ssn: holder.ssn || '',
        contactPerson: holder.contactPerson || '',
        phone: holder.phone || '',
        email: holder.email || '',
      },
    })
  }

  closeHolderDialog = () => {
    this.setState({ holderDialogOpen: false, editingHolderId: null })
  }

  handleHolderFormChange = (field, value) => {
    this.setState((prevState) => ({
      holderForm: { ...prevState.holderForm, [field]: value },
    }))
  }

  handlePersonSelect = (name, values) => {
    if (values && values.length > 0) {
      const person = values[values.length - 1]
      this.setState((prevState) => ({
        holderForm: {
          ...prevState.holderForm,
          personId: person.id,
          personName: (person.firstname || '') + ' ' + (person.lastname || ''),
          email: person.email || prevState.holderForm.email,
          phone: person.phone || prevState.holderForm.phone,
        },
      }))
    }
  }

  saveHolder = async () => {
    const { editingHolderId, holderForm } = this.state
    const data = { ...holderForm }

    if (editingHolderId) {
      data.id = editingHolderId
      await this.props.firebase.updateShareholder(data)
    } else {
      await this.props.firebase.createShareholder(data)
    }

    this.closeHolderDialog()
    this.loadAllData()
  }

  // Batch edit dialog
  openBatchEditDialog = () => {
    const { selectedShareIds, shares } = this.state
    const selectedShares = shares.filter((s) => selectedShareIds.includes(s.id))

    // Pre-fill with properties from first selected share, or empty if they differ
    let initialProperties = []
    if (selectedShares.length > 0) {
      const first = selectedShares[0].properties
      if (first && Array.isArray(first)) {
        const allSame = selectedShares.every((s) => {
          const p = s.properties || []
          return JSON.stringify(p) === JSON.stringify(first)
        })
        initialProperties = allSame
          ? first.map((p) => ({ ...p }))
          : first.map((p) => ({ key: p.key, value: '' }))
      }
    }

    this.setState({
      batchEditDialogOpen: true,
      batchEditProperties: initialProperties,
    })
  }

  closeBatchEditDialog = () => {
    this.setState({ batchEditDialogOpen: false })
  }

  handleBatchEditPropertyChange = (index, field, value) => {
    this.setState((prevState) => {
      const properties = [...prevState.batchEditProperties]
      properties[index] = { ...properties[index], [field]: value }
      return { batchEditProperties: properties }
    })
  }

  addBatchEditProperty = () => {
    this.setState((prevState) => ({
      batchEditProperties: [...prevState.batchEditProperties, { key: '', value: '' }],
    }))
  }

  removeBatchEditProperty = (index) => {
    this.setState((prevState) => ({
      batchEditProperties: prevState.batchEditProperties.filter((_, i) => i !== index),
    }))
  }

  saveBatchEdit = async () => {
    const { selectedShareIds, batchEditProperties } = this.state
    if (!selectedShareIds.length) return

    await this.props.firebase.updateSharesBatchProperties(selectedShareIds, batchEditProperties)
    this.closeBatchEditDialog()
    this.setState({ selectionModel: { type: 'include', ids: new Set() }, selectedShareIds: [] })
    this.loadAllData()
  }

  // Transaction dialog (multi-select)
  openTransactionDialog = () => {
    const { selectedShareIds, shares } = this.state
    const selectedShares = shares.filter((s) => selectedShareIds.includes(s.id))

    const ownerIds = [...new Set(selectedShares.map((s) => s.currentOwnerId).filter(Boolean))]
    const fromId = ownerIds.length === 1 ? ownerIds[0] : ''

    this.setState({
      transactionDialogOpen: true,
      transaction: {
        type: 'buy',
        date: moment().format('YYYY-MM-DD'),
        fromId,
        toId: '',
        price: '',
        note: '',
      },
    })
  }

  closeTransactionDialog = () => {
    this.setState({ transactionDialogOpen: false })
  }

  handleTransactionChange = (field, value) => {
    this.setState((prevState) => ({
      transaction: { ...prevState.transaction, [field]: value },
    }))
  }

  saveTransaction = async () => {
    const { selectedShareIds, transaction } = this.state
    if (!selectedShareIds.length) return

    const newTransaction = {
      ...transaction,
      price: transaction.price ? Number(transaction.price) : 0,
    }

    await this.props.firebase.updateSharesBatchTransaction(selectedShareIds, newTransaction)
    this.closeTransactionDialog()
    this.setState({ selectionModel: { type: 'include', ids: new Set() }, selectedShareIds: [] })
    this.loadAllData()
  }

  // Column definitions
  getPropertyKeys = () => {
    const keys = new Set()
    this.state.shares.forEach((s) => {
      if (s.properties && Array.isArray(s.properties)) {
        s.properties.forEach((p) => { if (p.key) keys.add(p.key) })
      }
    })
    return Array.from(keys)
  }

  getShareColumns = () => {
    const propertyColumns = this.getPropertyKeys().map((key) => ({
      headerName: key,
      field: 'prop_' + key,
      minWidth: 140,
      valueGetter: (value, row) => {
        if (!row.properties || !Array.isArray(row.properties)) return ''
        const prop = row.properties.find((p) => p.key === key)
        return prop ? prop.value : ''
      },
    }))

    return [
      { headerName: 'Osakenumero', field: 'shareNumber', minWidth: 130 },
      {
        headerName: 'Osakelajit',
        field: 'classId',
        minWidth: 160,
        valueGetter: (value) => this.getClassName(value),
      },
      {
        headerName: 'Omistaja',
        field: 'currentOwnerId',
        minWidth: 200,
        valueGetter: (value) => this.getOwnerName(value),
      },
      {
        headerName: 'Äänimäärä',
        field: 'votingpower',
        minWidth: 120,
        valueGetter: (value, row) => {
          const cls = this.state.classes.find((c) => c.id === row.classId)
          return cls ? cls.votingpower : '-'
        },
      },
      {
        headerName: 'Osakekirja',
        field: 'certificateId',
        minWidth: 160,
        valueGetter: (value) => this.getCertificateName(value),
      },
      ...propertyColumns,
      {
        headerName: 'Tapahtumat',
        field: 'transactionCount',
        minWidth: 120,
        valueGetter: (value, row) => {
          return row.transactions ? row.transactions.length : 0
        },
      },
      {
        headerName: ' ',
        field: 'delete',
        renderCell: (params) => (
          <div onClick={(e) => e.stopPropagation()}>
            <DeleteButton onClick={() => this.handleDelete(params.row.id, 'share')} />
          </div>
        ),
        width: 100,
        sortable: false,
        filterable: false,
      },
    ]
  }

  getSharesByHolderRows = () => {
    const { shares, holders } = this.state
    const holderMap = {}

    // Group shares by currentOwnerId
    shares.forEach((s) => {
      const ownerId = s.currentOwnerId || '_unassigned'
      if (!holderMap[ownerId]) {
        holderMap[ownerId] = {
          id: ownerId,
          holderId: ownerId,
          shareNumbers: [],
          shareCount: 0,
          totalVotes: 0,
          classIds: new Set(),
        }
      }
      holderMap[ownerId].shareNumbers.push(s.shareNumber)
      holderMap[ownerId].shareCount++
      if (s.classId) holderMap[ownerId].classIds.add(s.classId)
      const cls = this.state.classes.find((c) => c.id === s.classId)
      if (cls && cls.votingpower) holderMap[ownerId].totalVotes += Number(cls.votingpower) || 0
    })

    return Object.values(holderMap).map((row) => ({
      ...row,
      holderName: row.holderId === '_unassigned' ? 'Ei omistajaa' : this.getOwnerName(row.holderId),
      shareRange: this.formatShareRange(row.shareNumbers),
      classNames: Array.from(row.classIds).map((id) => this.getClassName(id)).join(', '),
    }))
  }

  formatShareRange = (numbers) => {
    if (!numbers || numbers.length === 0) return '-'
    const sorted = [...numbers].sort((a, b) => a - b)
    if (sorted.length === 1) return String(sorted[0])

    const ranges = []
    let start = sorted[0]
    let end = sorted[0]
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === end + 1) {
        end = sorted[i]
      } else {
        ranges.push(start === end ? String(start) : start + '-' + end)
        start = sorted[i]
        end = sorted[i]
      }
    }
    ranges.push(start === end ? String(start) : start + '-' + end)
    return ranges.join(', ')
  }

  getSharesByHolderColumns = () => [
    { headerName: 'Omistaja', field: 'holderName', minWidth: 250 },
    { headerName: 'Osakkeita', field: 'shareCount', minWidth: 120 },
    { headerName: 'Osakenumerot', field: 'shareRange', minWidth: 250 },
    { headerName: 'Äänimäärä', field: 'totalVotes', minWidth: 120 },
    { headerName: 'Osakelajit', field: 'classNames', minWidth: 200 },
  ]

  getCertificateName = (certificateId) => {
    if (!certificateId) return '-'
    const cert = this.state.certificates.find((c) => c.id === certificateId)
    return cert ? cert.name : '-'
  }

  // Certificate assign dialog
  openCertificateDialog = () => {
    this.setState({ certificateDialogOpen: true, certificateAssignId: '' })
  }

  closeCertificateDialog = () => {
    this.setState({ certificateDialogOpen: false, certificateAssignId: '' })
  }

  saveCertificateAssign = async () => {
    const { selectedShareIds, certificateAssignId } = this.state
    if (!selectedShareIds.length) return

    await this.props.firebase.updateSharesBatchCertificate(selectedShareIds, certificateAssignId)
    this.closeCertificateDialog()
    this.setState({ selectionModel: { type: 'include', ids: new Set() }, selectedShareIds: [] })
    this.loadAllData()
  }

  getSharesByCertificateRows = () => {
    const { shares } = this.state
    const certMap = {}

    shares.forEach((s) => {
      const certId = s.certificateId || '_unassigned'
      if (!certMap[certId]) {
        certMap[certId] = {
          id: certId,
          certificateId: certId,
          shareNumbers: [],
          shareCount: 0,
          totalVotes: 0,
          classIds: new Set(),
          ownerIds: new Set(),
        }
      }
      certMap[certId].shareNumbers.push(s.shareNumber)
      certMap[certId].shareCount++
      if (s.classId) certMap[certId].classIds.add(s.classId)
      if (s.currentOwnerId) certMap[certId].ownerIds.add(s.currentOwnerId)
      const cls = this.state.classes.find((c) => c.id === s.classId)
      if (cls && cls.votingpower) certMap[certId].totalVotes += Number(cls.votingpower) || 0
    })

    return Object.values(certMap).map((row) => ({
      ...row,
      certificateName: row.certificateId === '_unassigned' ? 'Ei osakekirjaa' : this.getCertificateName(row.certificateId),
      shareRange: this.formatShareRange(row.shareNumbers),
      classNames: Array.from(row.classIds).map((id) => this.getClassName(id)).join(', '),
      ownerNames: Array.from(row.ownerIds).map((id) => this.getOwnerName(id)).join(', '),
    }))
  }

  getSharesByCertificateColumns = () => [
    { headerName: 'Osakekirja', field: 'certificateName', minWidth: 200 },
    { headerName: 'Osakkeita', field: 'shareCount', minWidth: 120 },
    { headerName: 'Osakenumerot', field: 'shareRange', minWidth: 250 },
    { headerName: 'Äänimäärä', field: 'totalVotes', minWidth: 120 },
    { headerName: 'Omistajat', field: 'ownerNames', minWidth: 250 },
    { headerName: 'Osakelajit', field: 'classNames', minWidth: 200 },
  ]

  getHolderColumns = () => [
    {
      headerName: ' ',
      field: 'edit',
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()}>
          <EditButton onClick={() => this.handleEditHolder(params.row.id)} />
        </div>
      ),
      width: 120,
      sortable: false,
      filterable: false,
    },
    {
      headerName: 'Nimi',
      field: 'personName',
      minWidth: 200,
      valueGetter: (value, row) => this.getHolderName(row),
    },
    {
      headerName: 'Tyyppi',
      field: 'type',
      minWidth: 130,
      valueGetter: (value) =>
        value === 'person' ? 'Henkilö' : value === 'organization' ? 'Organisaatio' : '-',
    },
    { headerName: 'Hetu / Yhteyshenkilö', field: 'ssnOrContact', minWidth: 180, valueGetter: (value, row) => row.ssn || row.contactPerson || '-' },
    { headerName: 'Puhelin', field: 'phone', minWidth: 140 },
    { headerName: 'Sähköposti', field: 'email', minWidth: 200 },
    {
      headerName: 'Osakkeet',
      field: 'shareCount',
      minWidth: 100,
      valueGetter: (value, row) => this.getShareCount(row.id),
    },
    {
      headerName: ' ',
      field: 'delete',
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DeleteButton onClick={() => this.handleDelete(params.row.id, 'holder')} />
        </div>
      ),
      width: 100,
      sortable: false,
      filterable: false,
    },
  ]

  renderHolderDialog = () => {
    const { holderDialogOpen, holderForm, editingHolderId } = this.state

    return (
      <Dialog
        open={holderDialogOpen}
        onClose={this.closeHolderDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {editingHolderId ? 'Muokkaa osakkeenomistajaa' : 'Uusi osakkeenomistaja'}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            label='Tyyppi'
            value={holderForm.type}
            onChange={(e) => this.handleHolderFormChange('type', e.target.value)}
            fullWidth
            margin='normal'
          >
            <MenuItem value='person'>Henkilö</MenuItem>
            <MenuItem value='organization'>Organisaatio</MenuItem>
          </TextField>
          <PersonSearch
            label='Hae henkilö'
            onlyone
            values={
              holderForm.personId
                ? [{ id: holderForm.personId, firstname: holderForm.personName, lastname: '' }]
                : []
            }
            onChange={this.handlePersonSelect}
          />
          {holderForm.type === 'person' && (
            <TextField
              label='Henkilötunnus'
              value={holderForm.ssn}
              onChange={(e) => this.handleHolderFormChange('ssn', e.target.value)}
              fullWidth
              margin='normal'
            />
          )}
          {holderForm.type === 'organization' && (
            <TextField
              label='Yhteyshenkilö'
              value={holderForm.contactPerson}
              onChange={(e) => this.handleHolderFormChange('contactPerson', e.target.value)}
              fullWidth
              margin='normal'
            />
          )}
          <TextField
            label='Puhelin'
            value={holderForm.phone}
            onChange={(e) => this.handleHolderFormChange('phone', e.target.value)}
            fullWidth
            margin='normal'
          />
          <TextField
            label='Sähköposti'
            value={holderForm.email}
            onChange={(e) => this.handleHolderFormChange('email', e.target.value)}
            fullWidth
            margin='normal'
          />
        </DialogContent>
        <DialogActions>
          <Button color='secondary' onClick={this.closeHolderDialog}>
            Peruuta
          </Button>
          <Button color='primary' onClick={this.saveHolder}>
            Tallenna
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  renderTransactionDialog = () => {
    const { transactionDialogOpen, transaction, holders, selectedShareIds } = this.state

    return (
      <Dialog
        open={transactionDialogOpen}
        onClose={this.closeTransactionDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Kirjaa kauppa ({selectedShareIds.length} osaketta)</DialogTitle>
        <DialogContent>
          <TextField
            select
            label='Tyyppi'
            value={transaction.type}
            onChange={(e) => this.handleTransactionChange('type', e.target.value)}
            fullWidth
            margin='normal'
          >
            <MenuItem value='buy'>Osto</MenuItem>
            <MenuItem value='sell'>Myynti</MenuItem>
          </TextField>
          <TextField
            label='Päivämäärä'
            type='date'
            value={transaction.date}
            onChange={(e) => this.handleTransactionChange('date', e.target.value)}
            fullWidth
            margin='normal'
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label='Myyjä'
            value={transaction.fromId}
            onChange={(e) => this.handleTransactionChange('fromId', e.target.value)}
            fullWidth
            margin='normal'
          >
            <MenuItem value=''>-</MenuItem>
            {holders.map((h) => (
              <MenuItem key={h.id} value={h.id}>
                {this.getHolderName(h)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label='Ostaja'
            value={transaction.toId}
            onChange={(e) => this.handleTransactionChange('toId', e.target.value)}
            fullWidth
            margin='normal'
          >
            <MenuItem value=''>-</MenuItem>
            {holders.map((h) => (
              <MenuItem key={h.id} value={h.id}>
                {this.getHolderName(h)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label='Hinta (€)'
            type='number'
            value={transaction.price}
            onChange={(e) => this.handleTransactionChange('price', e.target.value)}
            fullWidth
            margin='normal'
          />
          <TextField
            label='Muistiinpano'
            value={transaction.note}
            onChange={(e) => this.handleTransactionChange('note', e.target.value)}
            fullWidth
            margin='normal'
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button color='secondary' onClick={this.closeTransactionDialog}>
            Peruuta
          </Button>
          <Button color='primary' onClick={this.saveTransaction}>
            Tallenna
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  renderBatchEditDialog = () => {
    const { batchEditDialogOpen, batchEditProperties, selectedShareIds } = this.state

    return (
      <Dialog
        open={batchEditDialogOpen}
        onClose={this.closeBatchEditDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Muokkaa lisätietoja ({selectedShareIds.length} osaketta)</DialogTitle>
        <DialogContent>
          {batchEditProperties.map((prop, index) => (
            <Row key={index} className='mb-1' style={{ alignItems: 'center' }}>
              <Col xs='5'>
                <TextField
                  label='Avain'
                  value={prop.key}
                  onChange={(e) => this.handleBatchEditPropertyChange(index, 'key', e.target.value)}
                  fullWidth
                  size='small'
                  margin='dense'
                />
              </Col>
              <Col xs='5'>
                <TextField
                  label='Arvo'
                  value={prop.value}
                  onChange={(e) => this.handleBatchEditPropertyChange(index, 'value', e.target.value)}
                  fullWidth
                  size='small'
                  margin='dense'
                />
              </Col>
              <Col xs='2'>
                <Button size='sm' color='danger' onClick={() => this.removeBatchEditProperty(index)}>
                  X
                </Button>
              </Col>
            </Row>
          ))}
          <Button size='sm' color='secondary' className='mt-2' onClick={this.addBatchEditProperty}>
            + Lisää kenttä
          </Button>
        </DialogContent>
        <DialogActions>
          <Button color='secondary' onClick={this.closeBatchEditDialog}>
            Peruuta
          </Button>
          <Button color='primary' onClick={this.saveBatchEdit}>
            Tallenna
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  renderCertificateDialog = () => {
    const { certificateDialogOpen, certificateAssignId, certificates, selectedShareIds } = this.state

    return (
      <Dialog
        open={certificateDialogOpen}
        onClose={this.closeCertificateDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Aseta osakekirja ({selectedShareIds.length} osaketta)</DialogTitle>
        <DialogContent>
          <TextField
            select
            label='Osakekirja'
            value={certificateAssignId}
            onChange={(e) => this.setState({ certificateAssignId: e.target.value })}
            fullWidth
            margin='normal'
          >
            <MenuItem value=''>Ei osakekirjaa</MenuItem>
            {certificates.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button color='secondary' onClick={this.closeCertificateDialog}>
            Peruuta
          </Button>
          <Button color='primary' onClick={this.saveCertificateAssign}>
            Tallenna
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  renderDataGrid = (rows, columns, suffix, extraProps) => {
    return (
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 280px)' }}>
        <div style={{ flexGrow: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            density='compact'
            showToolbar
            {...extraProps}
          />
        </div>
      </div>
    )
  }

  render() {
    if (!this.state.enabled) {
      return (
        <div className='content'>
          <Row>
            <Col md='12'>
              <ShareRegistryInfo onSubmit={this.enableModule} saving={this.state.saving} />
            </Col>
          </Row>
        </div>
      )
    }

    if (this.state.loading) {
      return (
        <div className='content'>
          <Row>
            <Col>Ladataan...</Col>
          </Row>
        </div>
      )
    }

    const { activeTab, shares, holders, selectionModel, selectedShareIds } = this.state

    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Tabs value={activeTab} onChange={this.handleTabChange}>
              <Tab label='Per osakekirja' />
              <Tab label='Per omistaja' />
              <Tab label='Per osake' />
              <Tab label='Osakkeenomistajat' />
            </Tabs>
          </Col>
        </Row>

        <Row className='mb-2'>
          <Col>
            {activeTab === 2 && selectedShareIds.length > 0 && (
              <>
                <Button
                  color='info'
                  onClick={this.openTransactionDialog}
                >
                  Kauppa ({selectedShareIds.length} osaketta)
                </Button>
                <Button
                  color='warning'
                  className='ml-2'
                  onClick={this.openBatchEditDialog}
                >
                  Muokkaa lisätietoja ({selectedShareIds.length})
                </Button>
                <Button
                  color='success'
                  className='ml-2'
                  onClick={this.openCertificateDialog}
                >
                  Aseta osakekirja ({selectedShareIds.length})
                </Button>
              </>
            )}
            {activeTab === 3 && (
              <Button color='primary' onClick={this.handleAddHolder}>
                Lisää osakkeenomistaja
              </Button>
            )}
          </Col>
        </Row>

        <Row>
          <Col xs='12'>
            {activeTab === 0 && this.renderDataGrid(this.getSharesByCertificateRows(), this.getSharesByCertificateColumns(), 'per-osakekirja')}
            {activeTab === 1 && this.renderDataGrid(this.getSharesByHolderRows(), this.getSharesByHolderColumns(), 'per-omistaja')}
            {activeTab === 2 &&
              this.renderDataGrid(shares, this.getShareColumns(), 'osakkeet', {
                checkboxSelection: true,
                disableSelectionOnClick: true,
                rowSelectionModel: selectionModel,
                onRowSelectionModelChange: (model) => {
                  const ids = model && model.ids ? Array.from(model.ids) : []
                  this.setState({ selectionModel: model, selectedShareIds: ids })
                },
              })}
            {activeTab === 3 && this.renderDataGrid(holders, this.getHolderColumns(), 'osakkeenomistajat')}
          </Col>
        </Row>

        {this.renderHolderDialog()}
        {this.renderBatchEditDialog()}
        {this.renderTransactionDialog()}
        {this.renderCertificateDialog()}
      </div>
    )
  }
}

export default withFirebase(ShareRegistryContainer)
